import React, { useMemo } from "react";
import { Container, Box, Typography, Grid, Card, CardContent, CardActions, Button, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { z } from "zod";
import { apiClientHooks } from "../../bootstrapping/InitApiClient";
import { useSelectedCustomer } from "../CustomerSelector";
import { useCaseWorkerContext } from "../../components/caseWorkers/caseWorkerContext";
import { schemas } from "../../generated/api/client";

export type DisplayCardProps = {
  title: string;
  count?: number;
  breakdown?: { title: string, count?: number, link?: string }[];
  linkList?: string;
  linkNew?: string;
}
export const DisplayCard: React.FC<DisplayCardProps> = props => {
  const navigate = useNavigate()

  const buttons = useMemo(() => {
    return <>
      {props.linkList && <Button size="small" onClick={() => props.linkList && navigate(props.linkList)}>📋 See All</Button>}
      {props.linkNew && <Button size="small" onClick={() => props.linkNew && navigate(props.linkNew)}>➕ Add New</Button>}
    </>
  }, [navigate, props.linkList, props.linkNew])

  return (
    <Card variant="outlined" sx={{ backgroundColor: '#e9e9e9' }}>
      <CardContent>
        <Typography sx={{ fontSize: 14 }} color="text.secondary" gutterBottom>
          {props.title}
        </Typography>
        <Typography variant="h5" component="div">
          {props.count ?? <>&nbsp;</> /* holding the visual space while data loads */}
        </Typography>
        <Stack sx={{ marginTop: 1 }}>
          {props.breakdown?.map((item, index) =>
            !!item.link
              ? <div key={index} style={{ cursor: 'pointer' }} onClick={() => item.link && navigate(item.link)}>{item.title}: <strong>{item.count}</strong></div>
              : <div key={index}>{item.title}: <strong>{item.count}</strong></div>
          )}
        </Stack>
      </CardContent>
      <CardActions>
        {buttons}
      </CardActions>
    </Card>
  )
}

type ApplicationStats = z.infer<typeof schemas.ApplicationStats>
const totalApplications = (applications: ApplicationStats): number =>
  applications.totalDraft + applications.totalAwaitingCustomerApproval + applications.totalAwaitingPayment
  + applications.totalNeedsChanges + applications.totalAwaitingCountersigning + applications.totalAwaitingDBSSubmission
  + applications.totalAwaitingDBSResponse + applications.totalRejected + applications.totalRestarted
  + applications.totalInProgress + applications.totalCompleted

type Stats = z.infer<typeof schemas.ReturnType_GetGlobalStats>['stats']
type StatsCardsGridProps = {
  stats: Stats | undefined,
  linkTo: (path: string) => string | undefined,
  /** Appended to list links, e.g. `case_worker=<profile_id>` for a manager's own-cases view */
  linkQuerySuffix?: string,
  /** Show the Profiles card (the Case Worker section links to their related profiles) */
  showProfilesCard?: boolean,
}
const StatsCardsGrid: React.FC<StatsCardsGridProps> = ({ stats, linkTo, linkQuerySuffix, showProfilesCard }) => {
  const withSuffix = (path: string) => {
    if (!linkQuerySuffix) return linkTo(path)
    return linkTo(`${path}${path.includes('?') ? '&' : '?'}${linkQuerySuffix}`)
  }

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} sm={6} md={4}>
        <DisplayCard title="📨 Invites" count={stats?.invites.totalActiveApplicants} linkList={withSuffix("invites")} />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <DisplayCard
          title="📝 Applications: Draft"
          count={stats?.applications.totalDraft}
          linkList={withSuffix("applications?status=draft")}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <DisplayCard
          title="✅ Applications: Awaiting Customer Verification"
          count={stats?.applications.totalAwaitingCustomerApproval}
          linkList={withSuffix("applications?status=awaiting_customer_approval")}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <DisplayCard
          title="💰 Applications: Awaiting Payment"
          count={stats?.applications.totalAwaitingPayment}
          linkList={withSuffix("applications?status=awaiting_payment")}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <DisplayCard
          title="💰 Applications: Needs Changes"
          count={stats?.applications.totalNeedsChanges}
          linkList={withSuffix("applications?status=needs_changes")}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <DisplayCard
          title="💰 Applications: Awaiting Countersigning"
          count={stats?.applications.totalAwaitingCountersigning}
          linkList={withSuffix("applications?status=awaiting_countersigning")}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <DisplayCard
          title="🪣 Applications: Other States"
          count={stats === undefined ? undefined : stats.applications.totalAwaitingDBSSubmission + stats.applications.totalAwaitingDBSResponse + stats.applications.totalRejected + stats.applications.totalRestarted + stats.applications.totalInProgress + stats.applications.totalCompleted}
          breakdown={[
            { title: "Awaiting DBS Submission", count: stats?.applications.totalAwaitingDBSSubmission, link: withSuffix("applications?status=awaiting_submission_to_DBS") },
            { title: "Awaiting DBS Response", count: stats?.applications.totalAwaitingDBSResponse, link: withSuffix("applications?status=awaiting_response_from_DBS") },
            { title: "Rejected", count: stats?.applications.totalRejected, link: withSuffix("applications?status=rejected") },
            { title: "Restarted", count: stats?.applications.totalRestarted, link: withSuffix("applications?status=restarted") },
            { title: "In Progress", count: stats?.applications.totalInProgress, link: withSuffix("applications?status=in_progress") },
            { title: "Completed", count: stats?.applications.totalCompleted, link: withSuffix("applications?status=completed") },
          ]}
          linkList={withSuffix("applications")}
        />
      </Grid>
      {showProfilesCard && (
        <Grid item xs={12} sm={6} md={4}>
          <DisplayCard title="👥 Profiles" linkList={linkTo("profiles")} linkNew={linkTo("invites/add")} />
        </Grid>
      )}
    </Grid>
  )
}

export const Element: React.FC = () => {
  const selectedCustomer = useSelectedCustomer()
  const linkTo = (path: string) => selectedCustomer ? `/${selectedCustomer.customer_id}/${path}` : undefined

  const { isCaseWorker, caseWorkersEnabled, myProfileId } = useCaseWorkerContext()

  const getGlobalStats = apiClientHooks.useGetGlobalStats({
    queries: {
      customer_id: selectedCustomer?.customer_id,
    },
  }, {
    enabled: !!selectedCustomer?.customer_id,
  })
  const stats = getGlobalStats.data?.stats

  // A manager's own caseload — decides whether their Case Worker section shows at all.
  // (For flagged case workers the server already narrows the main stats call.)
  const getMyCaseStats = apiClientHooks.useGetGlobalStats({
    queries: {
      customer_id: selectedCustomer?.customer_id,
      assigned_to_me: true,
    },
  }, {
    enabled: !!selectedCustomer?.customer_id && caseWorkersEnabled === true && !isCaseWorker,
  })
  const myCaseStats = getMyCaseStats.data?.stats

  const caseWorkerSectionStats = isCaseWorker ? stats : myCaseStats
  const managerHasOwnCases = myCaseStats != null
    && (myCaseStats.invites.totalActiveApplicants + totalApplications(myCaseStats.applications)) > 0
  const showCaseWorkerSection = isCaseWorker || managerHasOwnCases

  return <>
    <Helmet>
      <title>Customer Homepage</title>
    </Helmet>
    <Container component="main">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h5" marginBottom={3}>
          {selectedCustomer?.customer_name}
        </Typography>
        {showCaseWorkerSection && <>
          <Typography component="h1" variant="h6" marginBottom={3}>
            Case Worker
          </Typography>
          <StatsCardsGrid
            stats={caseWorkerSectionStats}
            linkTo={linkTo}
            // A manager's links need the explicit filter; a case worker's lists arrive pre-filtered
            linkQuerySuffix={isCaseWorker ? undefined : `case_worker=${myProfileId}`}
            showProfilesCard={isCaseWorker}
          />
        </>}
        {isCaseWorker && (
          // Case workers see only their own caseload — no Customer Admin section
          <Box sx={{ marginBottom: 4 }} />
        )}
        {!isCaseWorker && <>
          <Typography component="h1" variant="h6" marginBottom={3} marginTop={showCaseWorkerSection ? 4 : 0}>
            Customer Admin
          </Typography>
          <StatsCardsGrid stats={stats} linkTo={linkTo} />
          <Typography component="h1" variant="h6" marginBottom={3}>
            Control Panel
          </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6} md={4}>
            <DisplayCard title="👥 Profiles" linkList={linkTo("profiles")} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DisplayCard title="🫅 Customers" count={stats?.customers.totalActive} linkList={linkTo("customers")} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DisplayCard title="🧑‍🏫 Job Roles" count={stats?.jobs.totalActive} linkList={linkTo("jobs")} linkNew={linkTo("jobs/add")} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DisplayCard
              title="📨 Invites"
              count={stats === undefined ? undefined : stats.invites.totalActiveStaff + stats.invites.totalActiveCustomers + stats.invites.totalActiveApplicants}
              breakdown={[
                { title: "Staff", count: stats?.invites.totalActiveStaff },
                { title: "Customers", count: stats?.invites.totalActiveCustomers },
                { title: "Applicants", count: stats?.invites.totalActiveApplicants },
              ]}
              linkList={linkTo("invites")}
              linkNew={linkTo("invites/add")}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DisplayCard
              title="📝 Applications"
              count={stats === undefined ? undefined : stats.applications.totalDraft + stats.applications.totalAwaitingCustomerApproval + stats.applications.totalNeedsChanges + stats.applications.totalAwaitingCountersigning + stats.applications.totalAwaitingDBSSubmission + stats.applications.totalAwaitingDBSResponse + stats.applications.totalRejected + stats.applications.totalRestarted + stats.applications.totalInProgress + stats.applications.totalCompleted}
              breakdown={[
                { title: "Draft", count: stats?.applications.totalDraft, link: linkTo("applications?status=draft") },
                { title: "Awaiting Customer Verification", count: stats?.applications.totalAwaitingCustomerApproval, link: linkTo("applications?status=awaiting_customer_approval") },
                { title: "Awaiting Payment", count: stats?.applications.totalAwaitingPayment, link: linkTo("applications?status=awaiting_payment") },
                { title: "Needs Changes", count: stats?.applications.totalNeedsChanges, link: linkTo("applications?status=awaiting_countersigning") },
                { title: "Awaiting Countersigning", count: stats?.applications.totalAwaitingCountersigning, link: linkTo("applications?status=awaiting_countersigning") },
                { title: "Awaiting DBS Submission", count: stats?.applications.totalAwaitingDBSSubmission, link: linkTo("applications?status=awaiting_submission_to_DBS") },
                { title: "Awaiting DBS Response", count: stats?.applications.totalAwaitingDBSResponse, link: linkTo("applications?status=awaiting_response_from_DBS") },
                { title: "Rejected", count: stats?.applications.totalRejected, link: linkTo("applications?status=rejected") },
                { title: "Restarted", count: stats?.applications.totalRestarted, link: linkTo("applications?status=restarted") },
                { title: "In Progress", count: stats?.applications.totalInProgress, link: linkTo("applications?status=in_progress") },
                { title: "Completed", count: stats?.applications.totalCompleted, link: linkTo("applications?status=completed") },
              ]}
              linkList={linkTo("applications")}
            />
          </Grid>
        </Grid>
        </>}
      </Box>
    </Container>
  </>
}